// components/Ads/DebugDownloadModalAd.tsx
"use client";
import React, { useEffect, useRef, useState } from "react";

declare global {
  interface Window { adsbygoogle?: any[]; }
}

export default function DebugDownloadModalAd({
  adClient = "ca-pub-8612844749010589",
  adSlot = "8735693575",
  adTest = false, // Changed to false for production testing
  width = 300,
  height = 250,
}: {
  adClient?: string;
  adSlot?: string|number;
  adTest?: boolean;
  width?: number;
  height?: number;
}) {
  const ref = useRef<HTMLModElement | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const isFilled = (el: HTMLModElement | null) => {
    if (!el) return false;
    // Check if already processed by AdSense
    const status = el.getAttribute("data-adsbygoogle-status");
    if (status && status !== "") return true;
    if (el.querySelector("iframe")) return true;
    return false;
  };

  useEffect(() => {
    const el = ref.current;
    // console.log("[DebugAd] mount", el, "hasInitialized:", hasInitialized);
    
    // Prevent double initialization
    if (!el || hasInitialized || isFilled(el)) {
      // console.log("[DebugAd] skipping - already initialized or filled");
      return;
    }

    const waitForScript = (): Promise<void> => new Promise((resolve) => {
      if (window.adsbygoogle) { 
        // console.log("[DebugAd] adsbygoogle ready"); 
        return resolve(); 
      }
      let waited = 0;
      const int = setInterval(() => {
        if (window.adsbygoogle) { 
          clearInterval(int); 
          // console.log("[DebugAd] adsbygoogle now present"); 
          return resolve(); 
        }
        waited += 100;
        if (waited > 5000) { 
          clearInterval(int); 
          // console.warn("[DebugAd] timeout waiting for adsbygoogle (5s)"); 
          resolve(); 
        }
      }, 100);
    });

    let cancelled = false;

    const attemptPush = async () => {
      if (!el || cancelled || hasInitialized) return;
      
      // console.log("[DebugAd] attemptPush. filled:", isFilled(el));

      if (isFilled(el)) { 
        // console.log("[DebugAd] already filled, skipping"); 
        return; 
      }

      await waitForScript();

      // Wait a bit for DOM to be fully ready
      await new Promise(resolve => setTimeout(resolve, 100));

      const rect = el.getBoundingClientRect();
      // console.log("[DebugAd] rect:", rect);

      if (!rect || rect.width <= 0 || rect.height <= 0) {
        // console.log("[DebugAd] size is 0, skipping push");
        return;
      }

      try {
        // console.log("[DebugAd] pushing to adsbygoogle");
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        setHasInitialized(true);
        // console.log("[DebugAd] pushed successfully");
      } catch (err) {
        console.error("[DebugAd] push error:", err);
      }
    };

    attemptPush();

    return () => { 
      cancelled = true; 
      // console.log("[DebugAd] cleanup"); 
    };
  }, []); // Empty dependency array - only run once on mount

  return (
    <div 
      className="w-[300px] min-w-[300px] max-w-[300px] h-[250px] min-h-[250px] max-h-[250px] flex-none overflow-hidden flex items-center justify-center"
      
    >
      <ins
        ref={ref}
        className="adsbygoogle"
        style={{ display: "block", width: "300px", height: "250px" }}
        data-ad-client={adClient}
        data-ad-slot={adSlot}
        data-ad-format="fixed"
        {...(adTest ? { "data-adtest": "on" } : {})}
      ></ins>
    </div>
  );
}